import React, { useState, useEffect } from "react";
import { Lead } from "@/context/LeadContext";
import { usePartners } from "@/context/PartnerContext";
import { useAssignment } from "@/context/AssignmentContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, Mail, MessageSquare, AlertCircle, Repeat, MapPin, RefreshCw } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { sendSMS } from "@/utils/smsUtils";
import { sendEmailNotification } from "@/utils/emailUtils";
import { phpApi } from "@/integrations/php/client";
import { calculateDistance } from "@/utils/geoUtils";

interface UnassignedLeadNotifierProps {
  leads: Lead[];
  onSuccess?: () => void;
}

const UnassignedLeadNotifier: React.FC<UnassignedLeadNotifierProps> = ({
  leads,
  onSuccess
}) => {
  const { partners, refreshPartners } = usePartners();
  const { getNextPartnerIdForAssignment } = useAssignment();
  const { toast } = useToast();
  const [selectedLeadId, setSelectedLeadId] = useState<string>("");
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<string>("sms");
  const [customSmsMessage, setCustomSmsMessage] = useState<string>("");
  const [customEmailMessage, setCustomEmailMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [refreshingPartners, setRefreshingPartners] = useState(false);
  const [sortedPartners, setSortedPartners] = useState<Array<{partner: any, distance?: number}>>([]);

  // Filter only leads that don't have a partnerId assigned
  const unassignedLeads = leads.filter(lead => !lead.partnerId);

  const selectedLead = unassignedLeads.find(lead => lead.id === selectedLeadId);

  useEffect(() => {
    if (selectedLead && selectedLead.latitude && selectedLead.longitude) {
      // Update partner list with distances when a lead with coordinates is selected
      sortPartnersByDistance(selectedLead.latitude, selectedLead.longitude);
    } else {
      // Reset sorted partners when no lead with coordinates is selected
      setSortedPartners(partners.map(partner => ({ partner })));
    }
  }, [selectedLeadId, selectedLead, partners]);

  const sortPartnersByDistance = (lat: number, lng: number) => {
    const partnersWithDistance = partners
      .filter(p => p.active)
      .map(partner => {
        let distance: number | undefined = undefined;
        
        if (partner.latitude && partner.longitude) {
          distance = calculateDistance(lat, lng, partner.latitude, partner.longitude);
        }
        
        return {
          partner,
          distance
        };
      });
    
    // Sort first by whether they have distance data, then by distance
    partnersWithDistance.sort((a, b) => {
      // If both have distance, sort by distance
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      // If only a has distance, a comes first
      if (a.distance !== undefined) return -1;
      // If only b has distance, b comes first
      if (b.distance !== undefined) return 1;
      // If neither has distance, sort by name
      return a.partner.name.localeCompare(b.partner.name);
    });
    
    setSortedPartners(partnersWithDistance);
  };

  // Function to suggest a partner using round robin
  const handleSuggestPartner = async () => {
    if (!selectedLead) {
      toast({
        title: "Προσοχή",
        description: "Παρακαλώ επιλέξτε πρώτα έναν ενδιαφερόμενο",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsLoading(true);
      const nextPartnerId = await getNextPartnerIdForAssignment(selectedLead.service);
      
      if (nextPartnerId) {
        setSelectedPartnerId(nextPartnerId);
        toast({
          title: "Προτεινόμενος συνεργάτης",
          description: "Συνεργάτης προτάθηκε με βάση το σύστημα κυκλικής ανάθεσης.",
        });
      } else {
        toast({
          title: "Προσοχή",
          description: "Δεν βρέθηκε κατάλληλος συνεργάτης για αυτόματη πρόταση.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error selecting partner via round robin:", error);
      toast({
        title: "Σφάλμα",
        description: "Υπήρξε πρόβλημα κατά την πρόταση συνεργάτη.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshPartners = async () => {
    try {
      setRefreshingPartners(true);
      await refreshPartners();
      toast({
        title: "Επιτυχία",
        description: "Η λίστα συνεργατών ανανεώθηκε επιτυχώς",
      });
    } catch (error) {
      console.error("Failed to refresh partners:", error);
      toast({
        title: "Σφάλμα",
        description: "Υπήρξε πρόβλημα κατά την ανανέωση των συνεργατών",
        variant: "destructive",
      });
    } finally {
      setRefreshingPartners(false);
    }
  };

  const getServiceLabel = (service: string) => {
    switch (service) {
      case "εξοικονομώ": return "Εξοικονομώ";
      case "αντλίεςΘερμότητας": return "Αντλίες Θερμότητας";
      case "φωτοβολταϊκά": return "Φωτοβολταϊκά";
      case "κλιματιστικά": return "Κλιματιστικά";
      case "φυσικόΑέριο": return "Φυσικό Αέριο";
      case "ηλιακά": return "Ηλιακά";
      case "άλλο": return "Άλλο";
      default: return service;
    }
  };

  const getTimeframeLabel = (timeframe: string) => {
    switch (timeframe) {
      case "άμεσα": return "Άμεσα";
      case "εντός3μηνών": return "Εντός 3 μηνών";
      case "κάνωΈρευνα": return "Κάνω έρευνα αγοράς";
      default: return timeframe;
    }
  };

  const generateDefaultSmsMessage = () => {
    if (!selectedLead) return "";
    
    return `Νέος ενδιαφερόμενος: ${selectedLead.name}, ${getServiceLabel(selectedLead.service)}. Διεύθυνση: ${selectedLead.address}. Χρονικό πλαίσιο: ${getTimeframeLabel(selectedLead.timeframe)}. Δείτε περισσότερες πληροφορίες: https://leads.lineweb.gr/lead/${selectedLead.id}`;
  };

  // Function to assign a partner to a lead
  const assignPartnerToLead = async (leadId: string, partnerId: string) => {
    try {
      const selectedPartner = partners.find(partner => partner.id === partnerId);
      if (!selectedPartner) {
        throw new Error("Ο συνεργάτης δεν βρέθηκε");
      }
      
      const { error } = await phpApi
        .from('leads')
        .update({ partner_id: partnerId })
        .eq('id', leadId);
      
      if (error) {
        throw new Error(`Error updating lead: ${error.message}`);
      }
      
      return selectedPartner.name;
    } catch (error) {
      console.error("Failed to assign partner:", error);
      throw error;
    }
  };

  const handleSendSMS = async () => {
    if (!selectedLead || !selectedPartnerId) {
      toast({
        title: "Προσοχή",
        description: "Παρακαλώ επιλέξτε ενδιαφερόμενο και συνεργάτη",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      const selectedPartner = partners.find(partner => partner.id === selectedPartnerId);
      
      if (!selectedPartner) {
        throw new Error("Ο συνεργάτης δεν βρέθηκε");
      }
      
      // Check if partner has opted out of SMS
      if (selectedPartner.sms_opt_out) {
        throw new Error("Ο συνεργάτης έχει απενεργοποιήσει τη λήψη SMS");
      }
      
      // Assign the partner to the lead
      await assignPartnerToLead(selectedLead.id, selectedPartnerId);
      
      const messageToSend = customSmsMessage || generateDefaultSmsMessage();
      
      const response = await sendSMS(selectedPartner.phone, messageToSend, selectedLead.id);
      
      if (response.status === "failed") {
        throw new Error(response.error || "Αποτυχία αποστολής SMS");
      }
      
      toast({
        title: "Επιτυχία",
        description: `Το SMS εστάλη στον συνεργάτη ${selectedPartner.name} και του ανατέθηκε ο ενδιαφερόμενος.`,
      });

      setCustomSmsMessage("");
      setSelectedLeadId("");
      setSelectedPartnerId("");
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Failed to send SMS:", error);
      toast({
        title: "Αποτυχία αποστολής",
        description: error instanceof Error ? error.message : "Υπήρξε πρόβλημα κατά την αποστολή του SMS.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedLead || !selectedPartnerId) {
      toast({
        title: "Προσοχή",
        description: "Παρακαλώ επιλέξτε ενδιαφερόμενο και συνεργάτη",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      const selectedPartner = partners.find(partner => partner.id === selectedPartnerId);
      
      if (!selectedPartner) {
        throw new Error("Ο συνεργάτης δεν βρέθηκε");
      }
      
      if (!selectedPartner.email) {
        throw new Error("Ο συνεργάτης δεν έχει καταχωρημένο email");
      }
      
      // Assign the partner to the lead
      await assignPartnerToLead(selectedLead.id, selectedPartnerId);
      
      const leadDetails = {
        name: selectedLead.name,
        service: getServiceLabel(selectedLead.service),
        phone: selectedLead.phone,
        address: selectedLead.address,
        timeframe: getTimeframeLabel(selectedLead.timeframe),
        comments: customEmailMessage || selectedLead.comments || ""
      };
      
      const response = await sendEmailNotification(
        selectedPartner.email,
        selectedLead.id,
        leadDetails
      );
      
      if (response.status === "failed") {
        throw new Error(response.error || "Αποτυχία αποστολής email");
      }
      
      toast({
        title: "Επιτυχία",
        description: `Το email εστάλη στον συνεργάτη ${selectedPartner.name} και του ανατέθηκε ο ενδιαφερόμενος.`,
      });

      setCustomEmailMessage("");
      setSelectedLeadId("");
      setSelectedPartnerId("");
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Failed to send email:", error);
      toast({
        title: "Αποτυχία αποστολής",
        description: error instanceof Error ? error.message : "Υπήρξε πρόβλημα κατά την αποστολή του email.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const activePartners = partners.filter(partner => partner.active);

  const renderPartnerOptOutStatus = (partner: any) => {
    if (partner?.sms_opt_out) {
      return (
        <div className="text-sm text-red-500 mt-1 flex items-center">
          <AlertCircle className="h-3 w-3 mr-1" />
          SMS απενεργοποιημένα
        </div>
      );
    }
    return null;
  };

  if (unassignedLeads.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Μη Ανατεθειμένοι Ενδιαφερόμενοι</CardTitle>
          <CardDescription>
            Αποστολή ειδοποιήσεων σε συνεργάτες για ενδιαφερόμενους χωρίς ανάθεση
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Καμία εκκρεμότητα</AlertTitle>
            <AlertDescription>
              Δεν υπάρχουν ενδιαφερόμενοι χωρίς ανάθεση αυτή τη στιγμή.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Μη Ανατεθειμένοι Ενδιαφερόμενοι</CardTitle>
        <CardDescription>
          Αποστολή ειδοποιήσεων σε συνεργάτες για ενδιαφερόμενους χωρίς ανάθεση
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Επιλογή Ενδιαφερόμενου</label>
            <Select value={selectedLeadId} onValueChange={(value) => {
              setSelectedLeadId(value);
              setSelectedPartnerId(""); // Clear partner selection when lead changes
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Επιλογή ενδιαφερόμενου" />
              </SelectTrigger>
              <SelectContent>
                {unassignedLeads.map((lead) => (
                  <SelectItem key={lead.id} value={lead.id}>
                    {lead.name} - {getServiceLabel(lead.service)}
                    {lead.latitude && lead.longitude && <MapPin className="inline-block ml-1 h-3 w-3" />}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedLead && (
            <div className="p-3 bg-muted rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="font-medium">Όνομα:</span> {selectedLead.name}</div>
                <div><span className="font-medium">Τηλέφωνο:</span> {selectedLead.phone}</div>
                <div><span className="font-medium">Διεύθυνση:</span> {selectedLead.address}</div>
                <div><span className="font-medium">ΤΚ:</span> {selectedLead.postalCode}</div>
                <div><span className="font-medium">Υπηρεσία:</span> {getServiceLabel(selectedLead.service)}</div>
                <div><span className="font-medium">Χρον. πλαίσιο:</span> {getTimeframeLabel(selectedLead.timeframe)}</div>
                {selectedLead.latitude && selectedLead.longitude && (
                  <div className="col-span-2 flex items-center">
                    <MapPin className="h-4 w-4 mr-1 text-green-500" />
                    <span className="text-green-600">Διαθέσιμη τοποθεσία GPS</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Επιλογή Συνεργάτη</label>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRefreshPartners}
                  disabled={refreshingPartners}
                  className="flex items-center"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${refreshingPartners ? 'animate-spin' : ''}`} />
                  {refreshingPartners ? "Ανανέωση..." : "Ανανέωση"}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleSuggestPartner}
                  disabled={!selectedLead || isLoading}
                  className="flex items-center"
                >
                  <Repeat className="h-4 w-4 mr-1" />
                  Κυκλική ανάθεση
                </Button>
              </div>
            </div>
            
            <Select value={selectedPartnerId} onValueChange={setSelectedPartnerId}>
              <SelectTrigger>
                <SelectValue placeholder="Επιλογή συνεργάτη" />
              </SelectTrigger>
              <SelectContent>
                {sortedPartners.length > 0 ? (
                  sortedPartners.map(({ partner, distance }) => (
                    <SelectItem 
                      key={partner.id} 
                      value={partner.id}
                      disabled={partner.sms_opt_out}
                    >
                      <div className="flex justify-between items-center w-full">
                        <span>{partner.name}</span>
                        {partner.sms_opt_out && (
                          <span className="text-xs text-red-500 ml-2">(SMS απενεργοποιημένα)</span>
                        )}
                        {distance !== undefined && (
                          <span className="ml-2 text-sm text-muted-foreground">
                            ({distance.toFixed(1)} χλμ)
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="none" disabled>
                    Δεν υπάρχουν ενεργοί συνεργάτες
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedPartnerId && (
            <div className="p-3 bg-muted rounded-lg">
              <div className="font-medium">{partners.find(p => p.id === selectedPartnerId)?.name}</div>
              <div className="text-sm text-muted-foreground">{partners.find(p => p.id === selectedPartnerId)?.phone}</div>
              {partners.find(p => p.id === selectedPartnerId)?.email && (
                <div className="text-sm text-muted-foreground">{partners.find(p => p.id === selectedPartnerId)?.email}</div>
              )}
              {renderPartnerOptOutStatus(partners.find(p => p.id === selectedPartnerId))}
              {selectedLead?.latitude && selectedLead?.longitude && partners.find(p => p.id === selectedPartnerId)?.latitude && partners.find(p => p.id === selectedPartnerId)?.longitude && (
                <div className="text-sm text-muted-foreground mt-1">
                  <MapPin className="inline-block mr-1 h-3 w-3" />
                  Απόσταση: {calculateDistance(
                    selectedLead.latitude, 
                    selectedLead.longitude, 
                    partners.find(p => p.id === selectedPartnerId)!.latitude!, 
                    partners.find(p => p.id === selectedPartnerId)!.longitude!
                  ).toFixed(1)} χλμ
                </div>
              )}
            </div>
          )}
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="sms">
                <MessageSquare className="h-4 w-4 mr-2" />
                SMS
              </TabsTrigger>
              <TabsTrigger value="email">
                <Mail className="h-4 w-4 mr-2" />
                Email
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="sms" className="space-y-4 pt-4">
              {selectedLead && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Προεπισκόπηση SMS</AlertTitle>
                  <AlertDescription className="mt-2 text-sm">
                    {customSmsMessage || generateDefaultSmsMessage()}
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Προσαρμοσμένο Μήνυμα (προαιρετικό)</label>
                <Textarea
                  placeholder="Εισάγετε προσαρμοσμένο μήνυμα SMS..."
                  value={customSmsMessage}
                  onChange={(e) => setCustomSmsMessage(e.target.value)}
                  disabled={!selectedLead}
                />
                <p className="text-xs text-gray-500">
                  Αφήστε κενό για αυτόματο μήνυμα με τα στοιχεία του ενδιαφερόμενου.
                </p>
              </div>
              
              {selectedPartnerId && partners.find(p => p.id === selectedPartnerId)?.sms_opt_out && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Προσοχή</AlertTitle>
                  <AlertDescription>
                    Ο συνεργάτης έχει απενεργοποιήσει τη λήψη SMS.
                  </AlertDescription>
                </Alert>
              )}
              
              <Button 
                onClick={handleSendSMS} 
                disabled={
                  !selectedLeadId || 
                  !selectedPartnerId || 
                  isLoading || 
                  (partners.find(p => p.id === selectedPartnerId)?.sms_opt_out ?? false)
                } 
                className="w-full mt-4"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                {isLoading ? "Αποστολή..." : "Αποστολή SMS"}
              </Button>
            </TabsContent>
            
            <TabsContent value="email" className="space-y-4 pt-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Επιπλέον Μήνυμα για το Email (προαιρετικό)</label>
                <Textarea
                  placeholder="Εισάγετε επιπλέον πληροφορίες για το email..."
                  value={customEmailMessage}
                  onChange={(e) => setCustomEmailMessage(e.target.value)}
                  disabled={!selectedLead}
                />
                <p className="text-xs text-gray-500">
                  Το email θα περιέχει όλες τις βασικές πληροφορίες του ενδιαφερόμενου.
                </p>
              </div>
              
              {selectedPartnerId && !partners.find(p => p.id === selectedPartnerId)?.email && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Προσοχή</AlertTitle>
                  <AlertDescription>
                    Ο επιλεγμένος συνεργάτης δεν έχει καταχωρημένο email.
                  </AlertDescription>
                </Alert>
              )}
              
              <Button 
                onClick={handleSendEmail} 
                disabled={
                  !selectedLeadId || 
                  !selectedPartnerId || 
                  isLoading || 
                  !partners.find(p => p.id === selectedPartnerId)?.email
                } 
                className="w-full mt-4"
              >
                <Mail className="h-4 w-4 mr-2" />
                {isLoading ? "Αποστολή..." : "Αποστολή Email"}
              </Button>
            </TabsContent>
          </Tabs>
        </div>
      </CardContent>
      
      <CardFooter className="flex justify-end">
        <div className="text-xs text-gray-500">
          Σημείωση: Η ειδοποίηση θα καταγραφεί στο ιστορικό ειδοποιήσεων και ο ενδιαφερόμενος θα ανατεθεί στον επιλεγμένο συνεργάτη.
        </div>
      </CardFooter>
    </Card>
  );
};

export default UnassignedLeadNotifier;
